'use client'

import { useFormatter, useTranslations } from 'next-intl'
import { Typography } from '@repo/web-ui/components/typography'
import { Link } from '@repo/web-ui/components/link'
import { File06Icon } from 'primitives/icons'
import { ArrowRightIcon } from 'primitives/icons/arrows'
import { AppRoutes } from '@repo/web-utils/app-routes'

import { useMyAccountCardVariant } from '../_hooks/use-my-account-card-variant'
import { Card, CardSkeleton } from './card'
import { MyAccountEmptyBlock } from './empty-block'
import { useStudioProjectsListProjectsQuery } from '@web/gen/user'
import { ProjectSortField, SortDirection } from '@web/gen/types'

export function MyAccountRecentStudioProjects() {
  const { title, description } = useMyAccountCardVariant('studio-projects')
  const t = useTranslations('pages.my_account.widgets')
  const format = useFormatter()
  const { data, isPending } = useStudioProjectsListProjectsQuery({
    input: {
      limit: 8,
      sort: { field: ProjectSortField.CreateTime, direction: SortDirection.Desc },
    },
  })

  if (isPending) {
    return <CardSkeleton />
  }

  const projects = data?.studioProjectsListProjects.projects ?? []

  if (!projects.length) {
    return <MyAccountEmptyBlock variant="studio-projects" />
  }
  return (
    <Card className="flex grow flex-col gap-4 pb-0">
      <div className="flex flex-col">
        <Typography variant="heading-3" className="capitalize text-gray-100">
          {title}
        </Typography>
        {description ? <Typography variant="body">{description}</Typography> : null}
      </div>
      <div className="flex flex-col gap-2">
        {projects.slice(0, 3).map(({ name, id, updateTime, createTime }) => {
          // A reference point in time
          const now = new Date()
          const dateTime = updateTime || createTime ? new Date(updateTime || createTime) : now
          return (
            <Link
              key={id}
              href={`${AppRoutes.STUDIO_TOOL_CREATE}${id}`}
              className="flex gap-3 rounded p-1 hover:bg-gray-900"
            >
              <File06Icon className="h-7 w-7 shrink-0 !stroke-2 text-gray-100" />
              <span className="flex grow flex-col gap-0.5 truncate">
                <Typography variant="heading-4" className="text-14 truncate font-medium">
                  {name}
                </Typography>
                <Typography variant="body" className="text-12 text-gray-300">
                  {t(updateTime ? 'updated' : 'created', {
                    date: format.relativeTime(dateTime, { now, style: 'narrow' }),
                  })}
                </Typography>
              </span>
            </Link>
          )
        })}
      </div>
      <Link
        href={AppRoutes.STUDIO_TOOL_PROJECTS}
        className="border-cf-sf-gray-250 text-cf-sf-gray-1000 text-14 mt-auto flex items-center justify-center gap-1 border-t p-4 text-center font-semibold hover:underline"
      >
        {t('show_all_projects')}
        <ArrowRightIcon className="h-4 w-4 text-gray-100" />
      </Link>
    </Card>
  )
}
